import type { GetOnboardingJourneyFilesQuery } from "~/gql/queries/onboardingJourney.generated";
import type { StatusMessage } from "~/helpers/onboardingUploadStatus";
import type { UploadRegistrationOutcome } from "~/hooks/mutations/useOnboardingJourneyMutations";

import { OnboardingJourneyJobState, type OnboardingReviewArea } from "~/gql/graphqlBase.generated";
import logger from "~/helpers/logger";
import { reviewAreaLabelKey } from "~/helpers/notificationPresentation";
import validateOnboardingFile, { MAX_ONBOARDING_FILE_MB, type OnboardingFileErrorCode } from "~/helpers/onboardingFile";
import extractUploadErrorMessage from "~/helpers/onboardingUploadStatus";

export type OutsideUpload = GetOnboardingJourneyFilesQuery["onboardingJourneyOutsideUploads"][number];

export interface SetupFileSource {
	sourceId: string;
	sourceVersion: number;
}

export interface PendingSetupRegistration extends SetupFileSource {
	fileName: string;
	journeyId: string;
	sessionReference: string | null;
}

export type SetupFileStatus = "failed" | "uploaded" | "uploading";

export interface SetupAttachment {
	failure?: StatusMessage;
	fileName: string;
	id: string;
	retryable: boolean;
	source?: SetupFileSource;
	status: SetupFileStatus;
}

export interface SetupFileLoaded {
	areaLabelKeys: string[];
	importedAt: string;
}

export interface SetupFileChip {
	failure?: StatusMessage;
	fileName: string;
	key: string;
	loaded?: SetupFileLoaded;
	retryable: boolean;
	status: SetupFileStatus;
}

export type OutsideUploadView = OutsideUpload & { loaded?: SetupFileLoaded };

export interface LoadedUpload {
	fileName: string;
	key: string;
	loaded: SetupFileLoaded;
}

export interface SetupFilesView {
	chips: SetupFileChip[];
	loadedUploads: LoadedUpload[];
	outsideUploads: OutsideUploadView[];
}

const SETUP_FILE_EXTENSIONS = [".csv", ".tsv", ".pdf", ".xls", ".xlsx", ".zip"];

const PENDING_REGISTRATIONS_KEY = "fernie-pending-setup-registrations";

const REJECTED_FILE_MESSAGE: Record<OnboardingFileErrorCode, StatusMessage> = {
	"too-large": { params: { maxMb: MAX_ONBOARDING_FILE_MB }, translationKey: "fernieChat.setupFiles.tooLarge" },
	"unsupported-type": { translationKey: "fernieChat.setupFiles.unsupportedType" }
};

const sourceKey = ({ sourceId, sourceVersion }: SetupFileSource): string => `${sourceId}:${sourceVersion}`;

const isSameRegistration = (a: PendingSetupRegistration, b: PendingSetupRegistration): boolean =>
	a.journeyId === b.journeyId && sourceKey(a) === sourceKey(b);

const loadPendingRegistrations = (): PendingSetupRegistration[] => {
	try {
		const stored: unknown = JSON.parse(localStorage.getItem(PENDING_REGISTRATIONS_KEY) || "[]");
		return Array.isArray(stored) ? stored : [];
	} catch {
		return [];
	}
};

const savePendingRegistrations = (registrations: PendingSetupRegistration[]): PendingSetupRegistration[] => {
	try {
		localStorage.setItem(PENDING_REGISTRATIONS_KEY, JSON.stringify(registrations));
	} catch (error) {
		logger.warn("Failed to keep fernie's pending setup file registrations", error);
	}
	return registrations;
};

const rememberPendingRegistration = (registration: PendingSetupRegistration): PendingSetupRegistration[] =>
	savePendingRegistrations([
		...loadPendingRegistrations().filter((stored) => !isSameRegistration(stored, registration)),
		registration
	]);

const forgetPendingRegistration = (registration: PendingSetupRegistration): PendingSetupRegistration[] =>
	savePendingRegistrations(loadPendingRegistrations().filter((stored) => !isSameRegistration(stored, registration)));

export interface RegistrationToReplay {
	registration: PendingSetupRegistration;
	sessionReference: string;
}

const registrationsToReplay = (
	pending: readonly PendingSetupRegistration[],
	openJourneyId: string,
	sessionId: string | null,
	inFlightKeys: ReadonlySet<string>
): RegistrationToReplay[] =>
	pending.flatMap((registration) => {
		const sessionReference = registration.sessionReference ?? sessionId;
		if (registration.journeyId !== openJourneyId || !sessionReference || inFlightKeys.has(sourceKey(registration)))
			return [];
		return [{ registration, sessionReference }];
	});

const isReplayableRegistrationOutcome = (outcome: UploadRegistrationOutcome): boolean => outcome === "unreachable";

export interface SessionOpenState {
	isActive: boolean;
	isSetupConversation: boolean;
	sessionId: string | null;
	view: "chat" | "history";
}

const shouldOpenSession = ({ isActive, isSetupConversation, sessionId, view }: SessionOpenState): boolean =>
	isActive && view === "chat" && isSetupConversation && sessionId === null;

const shouldAdoptOpenedSession = ({
	cancelled,
	sendInFlight
}: {
	cancelled: boolean;
	sendInFlight: boolean;
}): boolean => !cancelled && !sendInFlight;

const buildSetupAttachment = (file: File, id: string): SetupAttachment => {
	const validation = validateOnboardingFile(file, SETUP_FILE_EXTENSIONS);
	return validation.ok
		? { fileName: file.name, id, retryable: false, status: "uploading" }
		: {
				failure: REJECTED_FILE_MESSAGE[validation.errorCode],
				fileName: file.name,
				id,
				retryable: false,
				status: "failed"
			};
};

const describeUploadFailure = (error: unknown): StatusMessage => {
	const serverMessage = extractUploadErrorMessage(error);
	return serverMessage ? { text: serverMessage } : { translationKey: "fernieChat.setupFiles.uploadFailed" };
};

const withAttachment = (
	attachments: readonly SetupAttachment[],
	id: string,
	patch: Partial<SetupAttachment>
): SetupAttachment[] =>
	attachments.map((attachment) => (attachment.id === id ? { ...attachment, ...patch } : attachment));

const withoutSource = (attachments: readonly SetupAttachment[], source: SetupFileSource): SetupAttachment[] =>
	attachments.filter((attachment) => !attachment.source || sourceKey(attachment.source) !== sourceKey(source));

const afterRegistration = (
	attachments: readonly SetupAttachment[],
	source: SetupFileSource,
	outcome: UploadRegistrationOutcome
): SetupAttachment[] => (outcome === "refused" ? withoutSource(attachments, source) : [...attachments]);

type TenantUpload = NonNullable<GetOnboardingJourneyFilesQuery["tenant"]>["onboardingUploads"][number];

const loadedOf = (upload: TenantUpload | undefined): SetupFileLoaded | undefined => {
	if (!upload?.importedAt) return undefined;
	const areas: OnboardingReviewArea[] = upload.reviewAreas;
	return {
		areaLabelKeys: areas.map(reviewAreaLabelKey).filter((labelKey): labelKey is string => labelKey !== null),
		importedAt: upload.importedAt
	};
};

const loadedFor = (
	uploads: readonly TenantUpload[],
	source: SetupFileSource | undefined
): SetupFileLoaded | undefined =>
	source
		? loadedOf(
				uploads.find(
					(candidate) => candidate.sourceId === source.sourceId && candidate.sourceVersion === source.sourceVersion
				)
			)
		: undefined;

const buildUnrepresentedImportedUploads = (
	files: GetOnboardingJourneyFilesQuery | undefined,
	representedKeys: ReadonlySet<string>
): LoadedUpload[] => {
	const uploads = files?.tenant?.onboardingUploads ?? [];
	const supersededKeys = new Set(
		(files?.onboardingJourneyState?.jobs ?? [])
			.filter((job) => job.state === OnboardingJourneyJobState.Superseded)
			.map(sourceKey)
	);
	const newestLegacy = new Map<string, TenantUpload>();
	const sourced: TenantUpload[] = [];
	for (const upload of uploads) {
		if (upload.sourceId == null || upload.sourceVersion == null) {
			const existing = newestLegacy.get(upload.fileName);
			if (!existing || new Date(upload.occurredAt).getTime() > new Date(existing.occurredAt).getTime()) {
				newestLegacy.set(upload.fileName, upload);
			}
		} else {
			const key = sourceKey({ sourceId: upload.sourceId, sourceVersion: upload.sourceVersion });
			if (!representedKeys.has(key) && !supersededKeys.has(key)) sourced.push(upload);
		}
	}
	return [...sourced, ...newestLegacy.values()]
		.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
		.flatMap((upload) => {
			const loaded = loadedOf(upload);
			const key = `${upload.sourceId ?? upload.fileName}:${upload.sourceVersion ?? ""}`;
			return loaded ? [{ fileName: upload.fileName, key: `loaded:${key}`, loaded }] : [];
		});
};

const journeyFileChips = (
	files: GetOnboardingJourneyFilesQuery | undefined,
	shownKeys: ReadonlySet<string>
): SetupFileChip[] => {
	const uploads = files?.tenant?.onboardingUploads ?? [];
	return (files?.onboardingJourneyState?.jobs ?? []).flatMap((job) => {
		const key = sourceKey(job);
		if (job.state === OnboardingJourneyJobState.Superseded || shownKeys.has(key)) return [];
		const upload = uploads.find(
			(candidate) => candidate.sourceId === job.sourceId && candidate.sourceVersion === job.sourceVersion
		);
		return upload
			? [
					{
						fileName: upload.fileName,
						key,
						loaded: loadedFor(uploads, job),
						retryable: false,
						status: "uploaded" as const
					}
				]
			: [];
	});
};

const buildSetupFilesView = (
	attachments: readonly SetupAttachment[],
	pending: readonly PendingSetupRegistration[],
	files: GetOnboardingJourneyFilesQuery | undefined
): SetupFilesView => {
	const attachedKeys = new Set(
		attachments.flatMap((attachment) => (attachment.source ? [sourceKey(attachment.source)] : []))
	);
	const shownKeys = new Set([...attachedKeys, ...pending.map(sourceKey)]);
	const uploads = files?.tenant?.onboardingUploads ?? [];
	const attachmentChips = attachments.map(({ failure, fileName, id, retryable, source, status }) => ({
		failure,
		fileName,
		key: id,
		loaded: loadedFor(uploads, source),
		retryable,
		status
	}));
	const pendingChips = pending
		.filter((registration) => !attachedKeys.has(sourceKey(registration)))
		.map((registration) => ({
			fileName: registration.fileName,
			key: sourceKey(registration),
			loaded: loadedFor(uploads, registration),
			retryable: false,
			status: "uploaded" as const
		}));

	const journeyChips = journeyFileChips(files, shownKeys);
	const outsideUploads = (files?.onboardingJourneyOutsideUploads ?? []).filter(
		(upload) => !shownKeys.has(sourceKey(upload))
	);
	const representedKeys = new Set([
		...shownKeys,
		...journeyChips.map(({ key }) => key),
		...(files?.onboardingJourneyOutsideUploads ?? []).map(sourceKey)
	]);

	return {
		chips: [...attachmentChips, ...pendingChips, ...journeyChips],
		loadedUploads: buildUnrepresentedImportedUploads(files, representedKeys),
		outsideUploads: outsideUploads.map((upload) => ({ ...upload, loaded: loadedFor(uploads, upload) }))
	};
};

const setupFiles = {
	afterRegistration,
	buildSetupAttachment,
	buildSetupFilesView,
	buildUnrepresentedImportedUploads,
	describeUploadFailure,
	forgetPendingRegistration,
	isReplayableRegistrationOutcome,
	loadPendingRegistrations,
	PENDING_REGISTRATIONS_KEY,
	registrationsToReplay,
	rememberPendingRegistration,
	SETUP_FILE_ACCEPT: SETUP_FILE_EXTENSIONS.join(","),
	shouldAdoptOpenedSession,
	shouldOpenSession,
	sourceKey,
	withAttachment
};

export default setupFiles;
